// src/auth/useAuth.js
import { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../api/axios';

const AuthCtx = createContext(null);

async function tryRefreshToken() {
  try {
    const resp = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data.access_token || null;
  } catch {
    return null;
  }
}

function decodeUser(token) {
  try {
    const d = jwtDecode(token);
    const nowSec = Math.floor(Date.now() / 1000);
    if (d?.exp && d.exp < nowSec) return null;
    const role = d?.role || 'user';
    return {
      id:       d?.user_id || d?.id || null,
      sub:      d?.sub || d?.email || 'me',
      email:    d?.email || null,
      role,
      org_id:   d?.org_id || null,
      exp:      d?.exp || null,
      // Helpers role
      isSuperAdmin: role === 'super_admin',
      isAdmin:      role === 'admin' || role === 'super_admin',
      isOrgAdmin:   role === 'org_admin' || role === 'admin' || role === 'super_admin',
      isTeacher:    role === 'teacher' || role === 'org_admin' || role === 'admin' || role === 'super_admin',
      isStudent:    role === 'student' || role === 'user',
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('access_token') || null);
  const [user,  setUser]  = useState(() => {
    const t = localStorage.getItem('access_token');
    return t ? decodeUser(t) : null;
  });

  const login = useCallback((newToken) => {
    localStorage.setItem('access_token', newToken);
    api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
    setToken(newToken);
    setUser(decodeUser(newToken));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    delete api.defaults.headers.common['Authorization'];
    fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (!token) {
      tryRefreshToken().then(newToken => {
        if (newToken) login(newToken);
        else setUser(null);
      });
      return;
    }

    const decoded = decodeUser(token);
    if (!decoded) {
      tryRefreshToken().then(newToken => {
        if (newToken) login(newToken);
        else { logout(); }
      });
      return;
    }

    setUser(decoded);
    api.defaults.headers.common['Authorization'] = 'Bearer ' + token;

    // Refresh proactif si < 5 min restantes
    if (decoded.exp) {
      const remaining = decoded.exp - Math.floor(Date.now() / 1000);
      if (remaining < 300) {
        tryRefreshToken().then(newToken => { if (newToken) login(newToken); });
      }
    }
  }, [token, login, logout]);

  const value = useMemo(() => ({
    token,
    user,
    isAuthenticated: Boolean(token && user),
    login,
    logout,
  }), [token, user, login, logout]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
